import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { MarketingShell } from '@/components/marketing-shell';
import { TOOLS_BY_SLUG, type ToolDef } from './data';
import {
  getToolIndexHref,
  getToolSurfaceConfig,
  ToolShellProvider,
  ToolSuccessPanel,
} from './components';
import { ToolRunnerHost } from './components/tool-runner-host';
import { useTranslation } from '@/i18n';
import { localizeTool } from '@/i18n/localized-tool';
import { setPageSeo } from '@/lib/brand';

function applyDocumentSeoFor(tool: ToolDef): void {
  setPageSeo({ title: tool.metaTitle, description: tool.metaDescription });
}

export function ToolDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const tool = TOOLS_BY_SLUG[slug];
  const { t } = useTranslation();
  const displayTool = tool ? localizeTool(tool, t) : undefined;

  useEffect(() => {
    if (displayTool) applyDocumentSeoFor(displayTool);
  }, [displayTool?.metaDescription, displayTool?.metaTitle]);

  return (
    <ToolShellProvider toolSlug={tool?.slug ?? slug} surface="public">
      <MarketingShell className="text-foreground" surface="public-tools">
        <main className="pb-12 pt-4 sm:pt-6">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            {displayTool ? <PublicToolHeader tool={displayTool} /> : null}
            <section
              className="min-w-0 rounded-xl border border-border bg-card p-4 shadow-okra-xs sm:p-6"
              aria-label={
                displayTool
                  ? t('tools.detail.toolAria', { name: displayTool.name })
                  : t('tools.detail.fallbackAria')
              }
            >
              <ToolRunnerHost slug={slug} />
              {/* Inline per-tool success state. Replaces the post-download
                  popup: it sits in the page, confirms the result, and points
                  at sibling tools instead of leaving the funnel. */}
              <ToolSuccessPanel />
            </section>
            {displayTool ? <PublicToolReference tool={displayTool} /> : null}
          </div>
        </main>
      </MarketingShell>
    </ToolShellProvider>
  );
}

function PublicToolHeader({ tool }: { tool: ToolDef }) {
  const { t } = useTranslation();
  const isCloud = tool.processingType === 'cloud';
  const toolsHref = getToolIndexHref(getToolSurfaceConfig('public'));

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
      >
        <Link
          to={toolsHref}
          className="inline-flex min-h-11 items-center gap-1 rounded-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('tools.detail.allTools')}
        </Link>
        <ChevronRight className="h-4 w-4 text-border" />
        <span aria-current="page" className="font-medium text-foreground">
          {tool.name}
        </span>
      </nav>

      <header className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
              {tool.h1}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
              {tool.subhead || tool.description}
            </p>
          </div>
        </div>
        <div className="inline-flex w-fit shrink-0 items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground">
          {tool.badge ? (
            <>{tool.badge}</>
          ) : isCloud ? (
            <>{t('tools.detail.cloudProcessing', { defaultValue: 'Cloud processing' })}</>
          ) : (
            <>{t('tools.detail.browserOnly')}</>
          )}
        </div>
      </header>
    </>
  );
}

function PublicToolReference({ tool }: { tool: ToolDef }) {
  const { t } = useTranslation();
  if (!tool.steps?.length && !tool.faq?.length && !tool.guide) return null;

  return (
    <div className="mt-8 grid gap-8 border-t border-border pt-8 lg:grid-cols-2 lg:gap-12">
      {tool.steps?.length ? (
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-foreground">
            {t('tools.detail.howTo', { name: tool.name })}
          </h2>
          <ol className="mt-4 space-y-3">
            {tool.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      {tool.faq?.length ? (
        <section>
          <h2 className="text-lg font-semibold text-foreground">{t('tools.detail.faq')}</h2>
          <div className="mt-4 divide-y divide-border rounded-lg border border-border bg-card px-4">
            {tool.faq.map((item, i) => (
              <details key={i} className="group py-1">
                <summary className="cursor-pointer rounded-sm py-3 text-sm font-medium leading-6 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {item.q}
                </summary>
                <p className="pb-3 text-sm leading-6 text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}
      {tool.guide ? (
        <p className="text-sm leading-6 text-muted-foreground lg:col-span-2">
          Full guide:{' '}
          <a href={tool.guide.href} className="font-medium text-foreground underline underline-offset-4">
            {tool.guide.label}
          </a>
        </p>
      ) : null}
    </div>
  );
}
